import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, 
  Sparkles, 
  AlertTriangle, 
  Phone,
  ChevronRight,
  Loader2,
  Heart,
  Smile,
  Frown,
  Meh
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const quickPrompts = [
  "How do I do my knee exercises?",
  "I'm feeling discouraged today",
  "What should I do if I feel pain?",
  "Remind me about my next session"
];

const aiResponses = {
  "How do I do my knee exercises?": "Great question! Let me walk you through your knee exercises:\n\n**Knee Flexion Stretch:**\n1. Sit on a chair with your foot flat on the floor\n2. Slowly slide your foot back, bending your knee\n3. Hold for 30 seconds\n4. Repeat 10 times\n\n**Quad Strengthening:**\n1. Lie on your back with one knee bent\n2. Keep the other leg straight\n3. Lift the straight leg about 12 inches\n4. Hold for 5 seconds, lower slowly\n5. Repeat 15 times\n\nWould you like me to explain any of these in more detail? 💪",
  
  "I'm feeling discouraged today": "I hear you, and it's completely normal to have tough days during recovery. ❤️\n\nRemember:\n• You've already improved your recovery score by 36 points!\n• Your pain has decreased from 8 to 3\n• You've completed 12 sessions\n• You're on a 7-day streak!\n\nRecovery isn't always linear, but every small step counts. Your therapist Dr. Chen has noted your excellent progress.\n\nWould you like me to share some motivational stories from other patients who felt the same way?",
  
  "What should I do if I feel pain?": "Good question! Here's what to do if you experience pain:\n\n**Mild discomfort during exercise:**\n• This is normal - continue with caution\n• Reduce intensity if needed\n\n**Sharp or sudden pain:**\n• Stop the exercise immediately\n• Rest and apply ice if needed\n• Note down the activity and pain level\n\n**Pain score 7 or higher:**\n• Contact your therapist\n• Skip exercises until advised\n\n⚠️ **If you experience severe pain, chest pain, or difficulty breathing, please seek immediate medical attention or call emergency services.**\n\nWould you like me to notify Dr. Chen about any concerns?",
  
  "Remind me about my next session": "Of course! Here are your upcoming appointments:\n\n📅 **Next Session:**\nToday at 3:30 PM\nWith: Dr. Lisa Chen\nLocation: Physiomobile Clinic, Level 3\nType: In-person\n\nDon't forget to:\n• Bring comfortable clothes\n• Complete today's exercises beforehand\n• Note any questions for Dr. Chen\n\nWould you like me to send you a reminder 1 hour before?"
};

const moodOptions = [
  { icon: Smile, label: 'Good', color: 'text-success' },
  { icon: Meh, label: 'Okay', color: 'text-warning' },
  { icon: Frown, label: 'Not great', color: 'text-primary' },
];

export default function PatientCompanion() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showCheckIn, setShowCheckIn] = useState(true);
  const [showEmergencyDialog, setShowEmergencyDialog] = useState(false);
  const [painScore, setPainScore] = useState(null);
  const [mood, setMood] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = (content) => {
    if (!content.trim()) return;
    
    // Check for emergency keywords
    const emergencyKeywords = ['chest pain', 'can\'t breathe', 'emergency', 'severe pain', 'bleeding'];
    if (emergencyKeywords.some(keyword => content.toLowerCase().includes(keyword))) {
      setShowEmergencyDialog(true);
      return;
    }
    
    setMessages(prev => [...prev, { role: 'user', content }]);
    setInput('');
    setIsTyping(true);
    
    setTimeout(() => {
      const response = aiResponses[content] || 
        `I understand you're asking about "${content}". Let me help!\n\nBased on your recovery plan and progress, I'd recommend discussing this with Dr. Chen during your next session. In the meantime, continue with your prescribed exercises and track any changes.\n\nIs there anything specific I can help clarify? 😊`;
      
      setMessages(prev => [...prev, { role: 'assistant', content: response }]);
      setIsTyping(false);
    }, 1500);
  };

  const handleCheckIn = () => {
    if (painScore !== null && mood !== null) {
      const checkInMessage = `Daily check-in recorded:\n• Pain: ${painScore}/10\n• Mood: ${mood}\n\nThank you for checking in! ${painScore <= 3 ? 'Great to see your pain is low!' : painScore >= 7 ? 'I\'ve notified your therapist about your pain level.' : 'Keep up with your exercises and rest well.'}`;
      
      setMessages([{ role: 'assistant', content: checkInMessage }]);
      setShowCheckIn(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">AI Companion</h1>
          <p className="text-sm text-muted-foreground mt-1">Your 24/7 recovery assistant</p>
        </div>
        <Badge variant="outline" className="gap-1">
          <Heart className="w-3 h-3 text-primary" />
          Always here to help
        </Badge>
      </div>
      
      {/* Daily Check-in */}
      {showCheckIn && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="border-primary/20 bg-primary-light/30">
            <CardContent className="p-5">
              <h3 className="font-display font-semibold text-foreground mb-4">Daily Check-in</h3>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-2">How's your pain today? (0-10)</p>
                  <div className="flex gap-2 flex-wrap">
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                      <Button
                        key={num}
                        variant={painScore === num ? 'default' : 'outline'}
                        size="sm"
                        className="w-9 h-9 p-0"
                        onClick={() => setPainScore(num)}
                      >
                        {num}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <p className="text-sm text-muted-foreground mb-2">How are you feeling?</p>
                  <div className="flex gap-2">
                    {moodOptions.map((option) => (
                      <Button
                        key={option.label}
                        variant={mood === option.label ? 'default' : 'outline'}
                        className="flex-1 gap-2"
                        onClick={() => setMood(option.label)}
                      >
                        <option.icon className={`w-4 h-4 ${mood === option.label ? '' : option.color}`} />
                        {option.label}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <Button 
                  className="w-full" 
                  disabled={painScore === null || mood === null}
                  onClick={handleCheckIn}
                >
                  Submit Check-in
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
      
      {/* Chat Interface */}
      <Card className="overflow-hidden">
        <div className="flex flex-col h-[calc(100vh-380px)] min-h-[400px]">
          {/* Chat Header */}
          <div className="flex items-center gap-3 p-4 border-b border-border">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h3 className="font-display font-semibold text-foreground">Recovery Companion</h3>
              <p className="text-xs text-muted-foreground">Powered by AI • Always available</p>
            </div>
          </div>
          
          {/* Messages */}
          <div className="flex-1 p-4 overflow-y-auto scrollbar-thin space-y-4">
            {messages.length === 0 && (
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-primary-foreground" />
                  </div>
                  <div className="chat-bubble-ai">
                    <p className="text-sm">Hi Sarah! 👋 I'm your AI Recovery Companion. I'm here to help with your exercises, answer questions, and support your recovery journey.\n\nWhat would you like to know?</p>
                  </div>
                </div>
                
                <div className="pl-11 space-y-2">
                  <p className="text-xs text-muted-foreground">Quick questions:</p>
                  {quickPrompts.map((prompt, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      size="sm"
                      className="mr-2 mb-2 text-xs h-8"
                      onClick={() => handleSendMessage(prompt)}
                    >
                      <ChevronRight className="w-3 h-3 mr-1 text-primary" />
                      {prompt}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            
            <AnimatePresence>
              {messages.map((message, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : ''}`}
                >
                  {message.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4 text-primary-foreground" />
                    </div>
                  )}
                  <div className={message.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-ai'}>
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            
            {isTyping && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-primary-foreground" />
                </div>
                <div className="chat-bubble-ai">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Safety Notice */}
          <div className="px-4 py-2 bg-muted/50 border-t border-border">
            <p className="text-[10px] text-muted-foreground text-center">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              I cannot diagnose or provide emergency care. For emergencies, call 999.
            </p>
          </div>
          
          {/* Input */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(input)}
                placeholder="Ask me anything about your recovery..."
                className="flex-1"
              />
              <Button onClick={() => handleSendMessage(input)} disabled={!input.trim()}>
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>
      
      {/* Emergency Dialog */}
      <Dialog open={showEmergencyDialog} onOpenChange={setShowEmergencyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Important Safety Notice
            </DialogTitle>
            <DialogDescription className="pt-4">
              It sounds like you may be experiencing a medical emergency.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-destructive/10 rounded-lg border border-destructive/20">
              <p className="text-sm font-medium text-destructive mb-2">If this is an emergency:</p>
              <Button variant="destructive" className="w-full gap-2">
                <Phone className="w-4 h-4" />
                Call Emergency Services (999)
              </Button>
            </div>
            
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Or notify your therapist:</p>
              <Button variant="outline" className="w-full" onClick={() => setShowEmergencyDialog(false)}>
                Notify Dr. Lisa Chen
              </Button>
            </div>
            
            <Button variant="ghost" className="w-full" onClick={() => setShowEmergencyDialog(false)}>
              It's not an emergency
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
